'use client';

import { useCallback, useState } from "react"
import { DateTime } from "luxon"

import { useService } from "./AnonSignLayout";
import { useAxios, APIError } from "../../core/_axios";
import { signAnonServiceByUniqueIdentifier } from "../../core/_requests";
import useLocalizedCopy from "../../hooks/useLocalizedCopy";

export function SignService() {

    const {
        service
    } = useService()

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [errorMessage, setErrorMessage] = useState('')

    const [signedSuccessfully, setSignedSuccessfully] = useState(false)

    const [submittingSignature, setSubmittingSignature] = useState(false)

    const onFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (submittingSignature) {
            return
        }
        setSubmittingSignature(true)
        setSignedSuccessfully(false)
        try {
            await signAnonServiceByUniqueIdentifier(axios, service.unique_identifier, {
                signature: {
                    full_name: event.currentTarget.fullName.value,
                    phone_number: event.currentTarget.phoneNumber.value,
                    email_address: event.currentTarget.emailAddress.value,
                    mailing_address_line1: event.currentTarget.mailingAddressLine1.value,
                    mailing_address_line2: event.currentTarget.mailingAddressLine2.value,
                    interested_in_planning_ahead: event.currentTarget.planningAhead.checked,
                }
            })
            setSignedSuccessfully(true)
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Errors.generic
            setErrorMessage(errorMessage)
            setSignedSuccessfully(false)
            setSubmittingSignature(false)
        }
    }, [axios, service.unique_identifier, submittingSignature])

    if (signedSuccessfully) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white space-y-6">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h2 className="text-3xl font-bold text-center text-gray-800">{copy.Public.sign.thankYouMessage}</h2>
            </div>
        );
    }

    return (
        <div>
            <div className="mt-4 mb-4 flex items-center justify-center">
                <img
                    src={service.public_data.deceased_person.avatar_url}
                    alt={service.public_data.deceased_person.full_name}
                    className="object-contain object-center bg-gray-200"
                    style={{ borderRadius: "50%", overflow: 'hidden', width: 175, height: 175 }}
                    width={175}
                    height={175}
                />
            </div>
            <div className="mb-4">
                <h2 className="text-3xl font-bold text-center text-gray-800">
                    {service.public_data.deceased_person.full_name}
                </h2>
                {service.public_data.deceased_person.birth_date || service.public_data.deceased_person.death_date ? <h3 className="text-lg font-semibold text-center text-gray-600">
                    {`${service.public_data.deceased_person.birth_date ? DateTime.fromISO(service.public_data.deceased_person.birth_date).toFormat('LLL d, yyyy') : ''} - ${service.public_data.deceased_person.death_date ? DateTime.fromISO(service.public_data.deceased_person.death_date).toFormat('LLL d, yyyy') : ''}`}
                </h3> : null}
            </div>
            <form onSubmit={onFormSubmit}>
                <fieldset>
                    <legend className="border-b border-gray-500 pb-4 mb-4">
                        {copy.Public.sign.signGuestbookLegend}
                    </legend>
                    <div className="mb-4">
                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">{copy.Public.sign.nameLabel}</label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            required
                            placeholder={copy.Public.sign.namePlaceholder}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">{copy.Public.sign.phoneLabel}</label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            placeholder={copy.Public.sign.phonePlaceholder}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700">{copy.Public.sign.emailLabel}</label>
                        <input
                            type="email"
                            id="emailAddress"
                            name="emailAddress"
                            placeholder={copy.Public.sign.emailPlaceholder}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="mailingAddress" className="block text-sm font-medium text-gray-700">{copy.Public.sign.mailingAddressLabel}</label>
                        <input
                            type="text"
                            id="mailingAddressLine1"
                            name="mailingAddressLine1"
                            placeholder={copy.Public.sign.mailingAddressLine1Placeholder}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        <input
                            type="text"
                            id="mailingAddressLine2"
                            name="mailingAddressLine2"
                            placeholder={copy.Public.sign.mailingAddressLine2Placeholder}
                            className="mt-4 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div className="mb-4" style={{ display: service.public_data.advanced_planning_check_is_hidden ? 'none' : 'block' }}>
                        <label htmlFor="planningAhead" className="block text-sm font-medium text-gray-700">
                            {service.public_data.mobile_advanced_planning_label_header || copy.Public.sign.interestedInPlanningLabel}
                        </label>
                        <div className="flex items-center mt-2">
                            <input
                                type="checkbox"
                                id="planningAhead"
                                name="planningAhead"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                defaultChecked={service.public_data.advanced_planning_check_default_checked || false}
                            />
                            <label htmlFor="planningAhead" className="ml-2 text-sm text-gray-600">
                                {service.public_data.mobile_advanced_planning_question || copy.Public.sign.interestedInPlanningText}
                            </label>
                        </div>
                    </div>

                </fieldset>

                <div className="p-4">
                    {errorMessage && <div className="text-red-500 text-sm">{errorMessage}</div>}

                    <div className="flex justify-end space-x-6 items-center">
                        <button
                            type="submit"
                            className={`px-4 py-2 rounded-md text-white ${submittingSignature ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'}`}
                            disabled={submittingSignature}
                        >
                            {submittingSignature ? copy.Public.sign.signing : copy.Public.sign.signGuestbook}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );

}
